(() => {
  if (window.VGCartTerms) return;

  const start = () => {
    const dialog = document.querySelector('[data-cart-terms-dialog]');
    const boxes = () => [...document.querySelectorAll('[data-cart-terms-checkbox]')];
    if (!dialog || !boxes().length) return;

    const message = dialog.querySelector('[data-cart-terms-message]');
    const title = dialog.querySelector('[data-cart-terms-title]');
    const dismiss = dialog.querySelector('[data-cart-terms-dismiss]');
    const keys = ['Terms accepted', 'Terms accepted at', 'Terms version', 'Terms URL', 'Terms acceptance source'];
    const root = window.Shopify?.routes?.root || '/';
    const state = { accepted: false, saved: false, saving: false, failed: false, generation: 0, fields: {} };
    const resuming = new WeakSet();
    let queue = Promise.resolve();
    let returnTo = null;

    const setText = (element, value) => {
      if (element && element.textContent !== value) element.textContent = value;
    };

    const refresh = () => {
      const ready = state.accepted && state.saved && !state.saving;
      boxes().forEach(box => {
        box.checked = state.accepted;
        const form = box.closest('form');
        if (!form) return;
        keys.slice(1).forEach(key => {
          let field = [...form.querySelectorAll('[data-cart-terms-field]')].find(input => input.name === `attributes[${key}]`);
          if (!field) {
            field = document.createElement('input');
            field.type = 'hidden';
            field.name = `attributes[${key}]`;
            field.dataset.cartTermsField = '';
            form.append(field);
          }
          field.value = state.fields[key] || '';
          field.disabled = !ready;
        });
      });
      document.querySelectorAll('[data-cart-terms-express]').forEach(gate => {
        gate.dataset.termsReady = String(ready);
        const wallets = gate.querySelector('[data-cart-terms-wallets]');
        const guard = gate.querySelector('[data-cart-terms-express-guard]');
        if (wallets) {
          wallets.inert = !ready;
          if (ready) wallets.removeAttribute('aria-hidden');
          else wallets.setAttribute('aria-hidden', 'true');
        }
        if (guard) guard.hidden = ready;
      });
      const status = state.saving ? dialog.dataset.saving : state.failed ? dialog.dataset.saveError : '';
      document.querySelectorAll('[data-cart-terms-status]').forEach(element => setText(element, status));
    };

    const show = (error = false, box = boxes().find(input => input.offsetParent !== null) || boxes()[0]) => {
      returnTo = box;
      setText(title, error ? dialog.dataset.errorTitle : dialog.dataset.title);
      setText(message, error ? dialog.dataset.saveError : dialog.dataset.required);
      if (!dialog.open) dialog.showModal();
      dismiss.focus();
    };

    const persist = () => {
      const generation = state.generation;
      const accepted = state.accepted;
      const attributes = Object.fromEntries(keys.map(key => [key, accepted ? state.fields[key] || '' : '']));
      state.saving = true;
      state.failed = false;
      refresh();
      const operation = queue.catch(() => {}).then(async () => {
        if (generation !== state.generation) return false;
        if (accepted && keys.some(key => !attributes[key])) throw new Error('Consent details are incomplete');
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 12000);
        try {
          const response = await fetch(root + 'cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes }),
            signal: controller.signal
          });
          if (!response.ok) throw new Error('Consent save failed');
          const cart = await response.json();
          if (generation !== state.generation) return false;
          if (!keys.every(key => (cart.attributes?.[key] || '') === attributes[key])) {
            throw new Error('Consent save was not confirmed');
          }
          state.saved = accepted;
          state.saving = false;
          state.failed = false;
          refresh();
          return true;
        } finally {
          window.clearTimeout(timer);
        }
      });
      queue = operation;
      operation.catch(() => {
        if (generation !== state.generation) return;
        state.saved = false;
        state.saving = false;
        state.failed = true;
        refresh();
      });
      return operation;
    };

    const select = accepted => {
      state.generation += 1;
      state.accepted = accepted;
      state.saved = false;
      state.failed = false;
      state.fields = accepted ? {
        'Terms accepted': 'Yes',
        'Terms accepted at': new Date().toISOString(),
        'Terms version': dialog.dataset.termsVersion,
        'Terms URL': dialog.dataset.termsUrl,
        'Terms acceptance source': 'Cart checkbox'
      } : {};
      refresh();
      return persist();
    };

    const ensureSaved = async () => {
      if (!state.accepted) return false;
      if (state.saved && !state.saving) return true;
      if (!state.saving) persist();
      await queue;
      return state.accepted && state.saved && !state.saving;
    };

    const resume = (form, button) => {
      if (resuming.has(form)) return;
      resuming.add(form);
      ensureSaved().then(ready => {
        if (!ready || !form.isConnected || !button?.isConnected) return;
        form.requestSubmit(button);
      }).catch(() => show(true, form.querySelector('[data-cart-terms-checkbox]')))
        .finally(() => resuming.delete(form));
    };

    const block = event => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    document.addEventListener('change', event => {
      if (!event.target.matches('[data-cart-terms-checkbox]')) return;
      select(event.target.checked).catch(() => {
        if (state.accepted) show(true, event.target);
      });
    }, true);

    document.addEventListener('click', event => {
      const guard = event.target.closest('[data-cart-terms-express-guard]');
      if (guard) {
        block(event);
        if (!state.accepted) show();
        else ensureSaved().catch(() => show(true));
        return;
      }
      const button = event.target.closest('[name="checkout"]');
      const form = button?.closest('form');
      const box = form?.querySelector('[data-cart-terms-checkbox]');
      if (!box) return;
      if (!state.accepted || !box.checked) {
        block(event);
        show(false, box);
      } else if (!state.saved || state.saving) {
        block(event);
        resume(form, button);
      }
    }, true);

    document.addEventListener('submit', event => {
      const form = event.target;
      const box = form.querySelector('[data-cart-terms-checkbox]');
      if (!box || (event.submitter && event.submitter.name !== 'checkout')) return;
      if (!state.accepted || !box.checked) {
        block(event);
        show(false, box);
      } else if (!state.saved || state.saving) {
        block(event);
        resume(form, event.submitter || form.querySelector('[name="checkout"]'));
      }
    }, true);

    document.addEventListener('invalid', event => {
      if (!event.target.matches('[data-cart-terms-checkbox]')) return;
      event.preventDefault();
      show(false, event.target);
    }, true);

    dismiss.addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-cart-terms-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('close', () => {
      setText(title, '');
      setText(message, '');
      if (returnTo?.isConnected) returnTo.focus();
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });

    const observer = new MutationObserver(refresh);
    document.querySelectorAll('[data-section-type="cart"], [data-section-type="quick-cart"]').forEach(element => {
      observer.observe(element, { childList: true, subtree: true });
    });
    window.addEventListener('pageshow', event => {
      if (event.persisted) select(false).catch(() => {});
    });
    window.VGCartTerms = { refresh };
    select(false).catch(() => {});
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
